import type { IBiomaterial } from '@zero-dash/types';
import {
  BIOMARKER_FETCH_CONCURRENCY,
  CHART_LANE_SERIES_COLOURS,
} from '@/constants/PatientDashboard/patientTimeline';
import type { IBiosample } from '@/types/Analysis/Biosamples.types';
import type { ISomaticSnv } from '@/types/SNV.types';
import type {
  BiomarkerSelection,
  IBiomarkerData,
  IBiomarkerFetchOutcome,
  IBiomarkerLine,
  IBiomarkerPoint,
  IChartLanePointFields,
} from '@/types/PatientDashboard/PatientTimeline.types';
import {
  buildChartLaneBiomaterialIndex,
  resolveChartLanePointFields,
} from './ChartLane.util';

interface IBiomarkerGroup {
  variantId: string;
  label: string;
  gene: string;
  points: IBiomarkerPoint[];
}

const buildDatedBiosampleIndex = (
  biosamples: IBiosample[],
  biomaterials: IBiomaterial[],
): Map<string, IChartLanePointFields> => {
  const biomaterialById = buildChartLaneBiomaterialIndex(biomaterials);

  const datedByBiosampleId = new Map<string, IChartLanePointFields>();
  biosamples.forEach((biosample) => {
    if (datedByBiosampleId.has(biosample.biosampleId)) {
      return;
    }
    const dated = resolveChartLanePointFields(
      biosample,
      biomaterialById.get(biosample.biomaterialId),
    );
    if (dated) {
      datedByBiosampleId.set(biosample.biosampleId, dated);
    }
  });
  return datedByBiosampleId;
};

export const getBiomarkerBiosampleIds = (
  biosamples: IBiosample[],
  biomaterials: IBiomaterial[],
): string[] => (
  Array.from(buildDatedBiosampleIndex(biosamples, biomaterials).keys())
);

export const buildBiomarkerData = (
  snvs: ISomaticSnv[],
  biosamples: IBiosample[],
  biomaterials: IBiomaterial[],
): IBiomarkerData => {
  const datedByBiosampleId = buildDatedBiosampleIndex(biosamples, biomaterials);

  const groupsByVariant = new Map<string, IBiomarkerGroup>();

  snvs.forEach((snv) => {
    if (!snv.reportable || !snv.depth || snv.depth <= 0 || snv.altad == null) {
      return;
    }
    const dated = datedByBiosampleId.get(snv.biosampleId);
    if (!dated) {
      return;
    }

    const point: IBiomarkerPoint = {
      id: `${snv.variantId}-${snv.biosampleId}`,
      biosampleId: snv.biosampleId,
      date: dated.date,
      vaf: (snv.altad / snv.depth) * 100,
      altad: snv.altad,
      depth: snv.depth,
      analyte: dated.analyte,
      assay: dated.assay,
    };

    const group = groupsByVariant.get(snv.variantId);
    if (group) {
      group.points.push(point);
    } else {
      groupsByVariant.set(snv.variantId, {
        variantId: snv.variantId,
        label: [snv.gene, snv.hgvs ?? snv.variantId].filter(Boolean).join(' '),
        gene: snv.gene,
        points: [point],
      });
    }
  });

  const lines: IBiomarkerLine[] = Array.from(groupsByVariant.values())
    .map((group) => ({
      ...group,
      points: [...group.points].sort((a, b) => a.date.localeCompare(b.date)),
    }))
    .sort((a, b) => a.gene.localeCompare(b.gene) || a.label.localeCompare(b.label))
    .map((group, index) => ({
      variantId: group.variantId,
      label: group.label,
      gene: group.gene,
      colour: CHART_LANE_SERIES_COLOURS[index % CHART_LANE_SERIES_COLOURS.length],
      points: group.points,
    }));

  return { lines };
};

export const fetchBiomarkerSnvs = async (
  biosampleIds: string[],
  fetchOne: (biosampleId: string) => Promise<ISomaticSnv[]>,
  signal?: AbortSignal,
): Promise<IBiomarkerFetchOutcome> => {
  const pending = [...biosampleIds];
  const collected: ISomaticSnv[] = [];
  let failedCount = 0;

  const runWorker = async (): Promise<void> => {
    const biosampleId = pending.shift();
    if (biosampleId === undefined || signal?.aborted) {
      return;
    }
    try {
      collected.push(...await fetchOne(biosampleId));
    } catch {
      failedCount += 1;
    }
    await runWorker();
  };

  await Promise.all(Array.from(
    { length: Math.min(BIOMARKER_FETCH_CONCURRENCY, biosampleIds.length) },
    () => runWorker(),
  ));

  return { snvs: collected, failedCount };
};

export const reconcileBiomarkerSelection = (
  data: IBiomarkerData,
  previous: BiomarkerSelection,
): BiomarkerSelection => {
  const next: BiomarkerSelection = {};
  data.lines.forEach((line) => {
    next[line.variantId] = previous[line.variantId] ?? true;
  });
  return next;
};

const outranks = (candidate: IBiomarkerPoint, current: IBiomarkerPoint): boolean => (
  candidate.vaf === current.vaf
    ? candidate.biosampleId < current.biosampleId
    : candidate.vaf > current.vaf
);

/**
 * The points one variant's line runs through: every measurement it has, across every
 * biosample and sequencing run, in date order. A date measured twice keeps the higher VAF
 * on the line and leaves the other measurement as an unconnected dot. A date the variant
 * was not measured at is simply absent, so the line runs straight through it.
 */
export const getBiomarkerLinePoints = (points: IBiomarkerPoint[]): IBiomarkerPoint[] => {
  const pointByDate = new Map<string, IBiomarkerPoint>();
  points.forEach((point) => {
    const current = pointByDate.get(point.date);
    if (!current || outranks(point, current)) {
      pointByDate.set(point.date, point);
    }
  });

  return Array.from(pointByDate.values()).sort((a, b) => a.date.localeCompare(b.date));
};
